// Resumo ao vivo do form de criação: mostra a regra como ela vai ficar, com os
// valores já formatados — é onde se confere "2,00%" antes de criar algo imutável.
import { CheckCircle2Icon } from "lucide-react"
import type { Control } from "react-hook-form"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import type { PaymentConfigurationAmountType } from "@/types/payment-configurations"

import type { CreatePaymentConfigurationValues } from "../schemas"
import {
  formatChargeAmount,
  parseChargeAmount,
  parseNonNegativeInteger,
} from "../utils"

const prefix = "pages.paymentConfigurations"

interface PaymentConfigurationSummarySidebarProps {
  control: Control<CreatePaymentConfigurationValues>
  whiteLabelNameOf: (whiteLabelId: string) => string
}

interface SummaryRowProps {
  label: string
  value: string
}

const SummaryRow = ({ label, value }: SummaryRowProps) => (
  <div>
    <p className="text-[11px] text-muted-foreground">
      {label}
    </p>
    <p className="truncate text-sm font-semibold tabular-nums">
      {value}
    </p>
  </div>
)

export const PaymentConfigurationSummarySidebar = ({
  control,
  whiteLabelNameOf,
}: PaymentConfigurationSummarySidebarProps) => {
  const { t, i18n } = useTranslation()
  const values = useWatch({ control })
  const empty = "—"

  const daysText = (raw: string | undefined) => {
    const days = parseNonNegativeInteger(raw ?? "")
    return days === null
      ? empty
      : t(`${prefix}.rules.days`, { count: days })
  }

  // Opcional vazio fica FORA do payload e o core não aplica regra nenhuma.
  const optionalDaysText = (raw: string | undefined) =>
    raw?.trim() ? daysText(raw) : t(`${prefix}.rules.none`)

  const chargeText = (
    enabled: boolean | undefined,
    charge:
      | {
          type?: PaymentConfigurationAmountType
          amount?: string
          daysAfterDue?: string
        }
      | undefined
  ) => {
    if (!enabled) return t(`${prefix}.rules.none`)
    if (!charge?.type) return empty
    const amount = parseChargeAmount(
      charge.amount ?? "",
      charge.type
    )
    if (amount === null) return empty
    const days = parseNonNegativeInteger(
      charge.daysAfterDue ?? ""
    )
    const formatted = formatChargeAmount(
      amount,
      charge.type,
      i18n.language
    )
    return days === null
      ? formatted
      : `${formatted} ${t(`${prefix}.rules.afterDays`, { count: days })}`
  }

  return (
    // Visível em TODO breakpoint: abaixo de xl o grid vira uma coluna e o
    // resumo cai depois do form — a conferência não pode sumir no notebook.
    <aside aria-label={t(`${prefix}.summary.title`)}>
      <div className="space-y-4 xl:sticky xl:top-4">
        <Card className="overflow-hidden border-0 shadow-md">
          <div className="border-b bg-muted/40 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t(`${prefix}.summary.title`)}
            </p>
          </div>
          <CardContent className="space-y-4 pt-4">
            <SummaryRow
              label={t(`${prefix}.fields.whiteLabel`)}
              value={
                values.whiteLabelId
                  ? whiteLabelNameOf(values.whiteLabelId)
                  : empty
              }
            />
            <SummaryRow
              label={t(`${prefix}.fields.description`)}
              value={values.description?.trim() || empty}
            />
            <SummaryRow
              label={t(`${prefix}.fields.daysDue`)}
              value={
                // Vazio fica FORA do payload: quem decide é o default do core.
                values.daysDue?.trim()
                  ? daysText(values.daysDue)
                  : t(`${prefix}.summary.defaultValue`)
              }
            />
            <SummaryRow
              label={t(`${prefix}.fields.daysAfterDue`)}
              value={daysText(values.daysAfterDue)}
            />
            <SummaryRow
              label={t(`${prefix}.fields.preChargeDays`)}
              value={optionalDaysText(values.preChargeDays)}
            />
            <SummaryRow
              label={t(
                `${prefix}.fields.pixAutoPreChargeDays`
              )}
              value={optionalDaysText(
                values.pixAutoPreChargeDays
              )}
            />
            <SummaryRow
              label={t(
                `${prefix}.fields.generateAllInvoices`
              )}
              value={
                values.generateAllInvoices
                  ? t("common.yes")
                  : t("common.no")
              }
            />
            <SummaryRow
              label={t(`${prefix}.fields.fine`)}
              value={chargeText(
                values.fineEnabled,
                values.fine
              )}
            />
            <SummaryRow
              label={t(`${prefix}.fields.interest`)}
              value={chargeText(
                values.interestEnabled,
                values.interest
              )}
            />
          </CardContent>
        </Card>
        <Card className="border border-dashed bg-muted/10">
          <CardContent className="pt-4">
            <div className="flex items-start gap-2">
              <CheckCircle2Icon
                className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                aria-hidden="true"
              />
              <p className="text-xs leading-relaxed text-muted-foreground">
                {t(`${prefix}.summary.hint`)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </aside>
  )
}
